import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import filterActiveStylists from 'bl/data/filterActiveStylists.bl';
import determineDisplayNumberOf from 'bl/timeoff/determineDisplayNumberOf.bl';
import Button from 'components/form/Button.component';
import S from 'constants/string.const';
import { useTranslation } from 'providers/GILT.provider';
import { selectTimeOffReport } from 'selectors/timeoff.selectors';
import { exportCSV } from 'utils/converters/csv.util';
import asObject from 'utils/objects/asObject.util';

const EMPTY = S.space;

export const ExportTimeOffButton = ({ stylists, headers, isHoursSelected }) => {
  const { t } = useTranslation();

  const report = useSelector(selectTimeOffReport);

  const tableHeaders = useMemo(() => [t('staffKpisReports.reportsTimeOff.headers.stylist'), ...headers], [headers, t]);

  const dataRows = useMemo(() => {
    const selfEmployedText = t('staffKpisReports.reportsTimeOff.selfEmployedStatus');
    const employedText = t('staffKpisReports.reportsTimeOff.employedStatus');

    const display = (hours, days) => determineDisplayNumberOf({ isHoursSelected, hours, days, defaultValue: EMPTY });

    return filterActiveStylists(stylists).map((stylist) => {
      const { id: stylistId, user_name: userName, is_self_employed: isSelf } = asObject(stylist);

      const displayStylist = `${userName} - ${isSelf ? selfEmployedText : employedText}`;

      const safeReport = asObject(report);
      const stylistDataObj = asObject(safeReport[stylistId]);

      const stylistData = Object.values(stylistDataObj).map((entry) => {
        const { categoryName, allowanceDays, allowanceHours, usedDays, usedHours, excessDays, excessHours, remainingDays, remainingHours, upcomingDays, upcomingHours } = entry;
        return [
          categoryName,
          display(allowanceHours, allowanceDays),
          display(usedHours, usedDays),
          display(upcomingHours, upcomingDays),
          display(remainingHours, remainingDays),
          display(excessHours, excessDays),
        ];
      });

      return {
        stylist: displayStylist,
        data: stylistData,
      };
    });
  }, [stylists, report, isHoursSelected, t]);

  const createCsvData = useCallback(() => {
    const csvData = dataRows.flatMap((stylistObj) =>
      stylistObj.data.map((row, rowIndex) =>
        Object.fromEntries(
          tableHeaders.map((key, i) => {
            if (key === tableHeaders[0]) return [key, rowIndex === 0 ? stylistObj.stylist : ''];
            return [key, row[i - 1]];
          }),
        ),
      ),
    );

    exportCSV({ data: csvData, fileName: t('staffKpisReports.reportsTimeOff.csvFileName') });
  }, [dataRows, t, tableHeaders]);

  return (
    <Button variant="primaryForce" onClick={createCsvData} width="small" height="medium">
      {t('staffKpisReports.reportsTimeOff.exportButton')}
    </Button>
  );
};
